/* eslint-disable @typescript-eslint/no-unused-vars */
import { react } from '@babel/types';
import { Button, Dropdown, Input, Tag } from 'antd';
import { PlusCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getInvoiceEmailList,
  modifyInvoiceEmailList,
} from '../../../../../api/billing-center/invoice';
import { AutoTable } from '../../../../../components/auto-table';
import { ConsoleDrawer } from '../../../../../components/drawer';
import {
  messageError,
  messageSuccess,
} from '../../../../../components/message';
import { ModalWrap } from '../../../../../components/modal-wrap';
import { isValidEmail } from '../../../../../lib';
import getFormatMessage from '../../../../../locales/get-format-message';
import { IAPIFeeCenterMailManageItem } from '../../../../../model/api-modules/bill-center/invoice';
import { IRootState } from '../../../../../store/reducers';
import styles from './index.module.scss';
import {
  CommonButton,
  CommonButtonGroup,
} from '../../../../../components/button/main';
import { IAPIStudioMemberHasPermissionItem } from '../../../../../model/api-modules';
import { PermissionCheck } from '../../../permission-check';
import { RbacRoleObjectKey } from '../../../../../model';

interface IProps {
  readonly owner: IAPIStudioMemberHasPermissionItem;
  readonly onClose: () => void;
  readonly onChange: (list: IAPIFeeCenterMailManageItem[]) => void;
}

interface IMailManageTableItem extends IAPIFeeCenterMailManageItem {
  isNew?: boolean;
  mock?: boolean;
}

export const InvoiceEmailDrawer = (props: IProps) => {
  const { owner } = props;
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [rawEmailList, setRawEmailList] = useState<
    ReadonlyArray<IAPIFeeCenterMailManageItem>
  >([]);
  const [emailList, setEmailList] = useState<
    ReadonlyArray<IMailManageTableItem>
  >([]);

  const [editRow, setEditRow] = useState<IMailManageTableItem>();

  useEffect(() => {
    init();
  }, [selectStudio?.studioID]);

  const init = async () => {
    setLoading(true);
    await getList();
    setLoading(false);
  };

  const getList = async () => {
    if (!selectStudio) {
      return;
    }
    const res = await getInvoiceEmailList(selectStudio.studioID);
    setRawEmailList(res);
    setEmailList(res);
    return res;
  };

  const add = () => {
    if (!selectStudio?.studioID) {
      return;
    }
    const createTime = new Date().toISOString();
    const newRow: IMailManageTableItem = {
      email: '',
      isDefault: false,
      studioID: selectStudio.studioID,
      createTime,
      isNew: true,
    };
    setEmailList([...emailList, newRow]);
    setEditRow(newRow);
  };

  const submit = async (list: IAPIFeeCenterMailManageItem[]) => {
    const defaultEmailIndex = list.findIndex((item) => item.isDefault);

    const sortList = [...list];
    sortList.splice(defaultEmailIndex, 1);
    sortList.unshift(list[defaultEmailIndex]);

    setEmailList(sortList);
    if (!selectStudio?.studioID) {
      return;
    }
    const res = await modifyInvoiceEmailList({
      studioID: selectStudio.studioID,
      data: sortList,
    });
    if (!res) {
      messageError(getFormatMessage('save_failed'));
      return;
    }
    messageSuccess(getFormatMessage('save_success'));
    const resList = await getList();
    if (!resList) {
      return;
    }
    props.onChange?.([...resList]);
  };

  const setDefault = (record: IMailManageTableItem) => {
    const list = emailList.map((item) => {
      return {
        ...item,
        isDefault: item.email === record.email,
      };
    });
    submit(list);
  };
  const save = async (record: IMailManageTableItem, value = '') => {
    if (!record.isNew && !value) {
      messageError(getFormatMessage('请输入修改的邮箱'));
      return;
    }
    if (record.isNew && !value) {
      messageError(getFormatMessage('请输入邮箱'));
      return;
    }
    if (
      emailList.some(
        (email) =>
          email.email === value && email.createTime !== record.createTime
      )
    ) {
      messageError(getFormatMessage('邮箱已存在'));
      return;
    }

    if (!isValidEmail(value)) {
      messageError(getFormatMessage('邮箱格式不正确'));
      return;
    }
    setEditRow(undefined);
    if (record.isNew && !value) {
      setEmailList(emailList.filter((email) => !email.isNew));
      return;
    }
    const matched = rawEmailList.find(
      (email) => email.createTime === record.createTime
    );
    if (matched?.email === value) {
      setEmailList([...emailList]);
      return;
    }
    const newList: IAPIFeeCenterMailManageItem[] = emailList.map((email) => {
      if (email.isNew) {
        const newData = {
          ...email,
          email: value,
          createTime: new Date().toISOString(),
        };
        delete newData.isNew;
        return newData;
      }

      if (email.createTime === record.createTime) {
        return { ...email, email: value };
      }
      return { ...email };
    });

    submit(newList);
  };
  const cancelEmail = () => {
    setEditRow(undefined);
    setEmailList(emailList.filter((email) => !email.isNew));
  };

  const deleteEmail = (record: IMailManageTableItem) => {
    ModalWrap.warn({
      title: getFormatMessage('删除邮箱'),
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
      children: (
        <div style={{ whiteSpace: 'pre-line' }}>
          {getFormatMessage(`确定删除邮箱[${record.email}]么`)}
        </div>
      ),
      onOk: async () => {
        if (record.createTime === editRow?.createTime) {
          setEditRow(undefined);
        }
        let newMailList: IMailManageTableItem[] = [...emailList];
        if (record.isDefault) {
          newMailList = emailList.map((email) => {
            return { ...email, isDefault: email.email === owner.UserID };
          });
        }
        submit(
          newMailList.filter((email) => email.createTime !== record.createTime)
        );
        return true;
      },
    });
  };

  return (
    <ConsoleDrawer
      onClose={props.onClose}
      title={getFormatMessage('email_info')}
      className={styles.emailDrawer}
      cancelText={getFormatMessage('close')}
      hideOkButton={true}
    >
      <AutoTable
        loading={loading}
        list={
          emailList.length >= 20
            ? emailList
            : [
                ...emailList,
                {
                  mock: true,
                  createTime: '',
                  email: '',
                  isDefault: false,
                  studioID: '',
                },
              ]
        }
        configFields={[
          {
            key: 'email',
            name: getFormatMessage('email_title'),
            tableExtension: {
              renderRow(key, value, row) {
                if (row.mock) {
                  return (
                    <>
                      <PermissionCheck
                        permissionKey={RbacRoleObjectKey.FeeCenterEdit}
                        tipsPlacement="leftBottom"
                      >
                        <CommonButton
                          className={styles.addBtn}
                          type="link"
                          onClick={add}
                          disabled={!!editRow}
                        >
                          <PlusCircleOutlined />
                          {getFormatMessage('email_add')}
                        </CommonButton>
                      </PermissionCheck>
                    </>
                  );
                }
                if (editRow && editRow?.createTime === row.createTime) {
                  return (
                    <Input
                      autoFocus
                      defaultValue={value}
                      onChange={(ev) => {
                        setEditRow({ ...editRow, email: ev.target.value });
                      }}
                    />
                  );
                }
                return (
                  <div className={styles.emailCell}>
                    <span>{value}</span>
                    {row.isDefault && (
                      <Tag color="#5764f11a">
                        <span style={{ color: '#5764F1' }}>
                          {getFormatMessage('email_default')}
                        </span>
                      </Tag>
                    )}
                  </div>
                );
              },
            },
          },
          {
            key: 'operate',
            name: getFormatMessage('operate'),
            tableExtension: {
              width: '180px',
              renderRow(key, value, row) {
                if (row.mock) {
                  return null;
                }
                if (row.isNew) {
                  return (
                    <CommonButtonGroup>
                      <CommonButton
                        type="link"
                        onClick={() =>
                          save(row as IMailManageTableItem, editRow?.email)
                        }
                      >
                        {getFormatMessage('save')}
                      </CommonButton>
                      <CommonButton type="link" onClick={() => cancelEmail()}>
                        {getFormatMessage('cancel')}
                      </CommonButton>
                    </CommonButtonGroup>
                  );
                }
                if (editRow?.createTime === row.createTime) {
                  return (
                    <CommonButtonGroup>
                      <PermissionCheck
                        permissionKey={RbacRoleObjectKey.FeeCenterEdit}
                      >
                        <CommonButton
                          type="link"
                          onClick={() =>
                            save(row as IMailManageTableItem, editRow?.email)
                          }
                        >
                          {getFormatMessage('save')}
                        </CommonButton>
                      </PermissionCheck>
                      <CommonButton type="link" onClick={() => cancelEmail()}>
                        {getFormatMessage('cancel')}
                      </CommonButton>
                    </CommonButtonGroup>
                  );
                }
                return (
                  <CommonButtonGroup>
                    {!row.isDefault && (
                      <PermissionCheck
                        permissionKey={RbacRoleObjectKey.FeeCenterEdit}
                      >
                        <CommonButton
                          type="link"
                          onClick={() =>
                            setDefault(row as IMailManageTableItem)
                          }
                        >
                          {getFormatMessage('email_set_default')}
                        </CommonButton>
                      </PermissionCheck>
                    )}
                    <PermissionCheck
                      permissionKey={RbacRoleObjectKey.FeeCenterEdit}
                    >
                      <Dropdown
                        className={styles.moreBtn}
                        menu={{
                          items: [
                            {
                              label: (
                                <CommonButton type="link">
                                  {getFormatMessage('email_modify')}
                                </CommonButton>
                              ),
                              key: 'modify',
                              onClick() {
                                setEditRow(row as IMailManageTableItem);
                                setEmailList([...emailList]);
                              },
                            },
                            {
                              label: (
                                <CommonButton
                                  disabled={row.email === owner.UserID}
                                  type="link"
                                >
                                  {getFormatMessage('email_delete')}
                                </CommonButton>
                              ),
                              key: 'delete',
                              disabled: row.email === owner.UserID,
                              onClick() {
                                deleteEmail(row as IMailManageTableItem);
                              },
                            },
                          ],
                        }}
                      >
                        <MoreOutlined />
                      </Dropdown>
                    </PermissionCheck>
                  </CommonButtonGroup>
                );
              },
            },
          },
        ]}
      />
    </ConsoleDrawer>
  );
};
